import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
} from '@mui/material';
import { Notifications, Security, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { equipmentAPI, barriersAPI } from '../../services/api';
import toast from 'react-hot-toast';

function EquipmentBarriers() {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const [associations, setAssociations] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [barriers, setBarriers] = useState([]);


  const loadEquipments = useCallback(async () => {
    try {
      const response = await equipmentAPI.getAll();
      const equipmentsData = Array.isArray(response?.data?.data) ? response.data.data : 
                            Array.isArray(response?.data) ? response.data : [];
      setEquipments(equipmentsData);
    } catch (error) {
      const mockEquipments = [
        { id: '1', equipment_name: 'Desfibrilador DEA-001', status: 'expired' },
        { id: '2', equipment_name: 'Monitor Cardíaco MC-200', status: 'expiring' },
        { id: '3', equipment_name: 'Bomba de Infusão BI-150', status: 'valid' }
      ];
      setEquipments(mockEquipments);
    }
  }, []);

  const loadBarriers = useCallback(async () => {
    try {
      const response = await barriersAPI.getAll();
      const barriersData = Array.isArray(response?.data?.data) ? response.data.data : 
                          Array.isArray(response?.data) ? response.data : [];
      setBarriers(barriersData.filter(b => b.is_active));
    } catch (error) {
      const mockBarriers = [
        { id: '1', name: 'Entrada UTI' },
        { id: '2', name: 'Saída Emergência' },
        { id: '3', name: 'Centro Cirúrgico' }
      ];
      setBarriers(mockBarriers);
    }
  }, []);

  const loadAssociations = useCallback(() => {
    // Gera associações baseadas nos equipamentos que têm barreiras associadas
    const associationsData = equipments
      .filter(eq => eq.associated_barriers && eq.associated_barriers.length > 0)
      .map(equipment => {
        const barrierNames = equipment.associated_barriers.map(barrierId => 
          barriers.find(b => b.id === barrierId)?.name
        ).filter(Boolean);
        
        return {
          id: `assoc-${equipment.id}`,
          equipment_id: equipment.id,
          equipment_name: equipment.equipment_name || equipment.name,
          equipment_status: equipment.status,
          barriers: equipment.associated_barriers,
          barrier_names: barrierNames,
          alert_type: equipment.alert_type || 'always',
          last_detection: null,
          alerts_sent: 0
        };
      });
    
    setAssociations(associationsData);
  }, [equipments, barriers]);

  useEffect(() => {
    loadEquipments();
    loadBarriers();
  }, [loadEquipments, loadBarriers]);

  useEffect(() => {
    if (equipments.length > 0 && barriers.length > 0) {
      loadAssociations();
    }
  }, [equipments, barriers, loadAssociations]);



  const handleDelete = async (associationId) => {
    if (window.confirm('Tem certeza que deseja remover esta associação?')) {
      try {
        const association = associations.find(a => a.id === associationId);
        if (association) {
          const equipment = equipments.find(e => e.id === association.equipment_id);
          const updatedEquipment = {
            ...equipment,
            associated_barriers: [],
            alert_type: 'never'
          };
          
          await equipmentAPI.update(association.equipment_id, updatedEquipment);
          toast.success('Associação removida com sucesso!');
          await loadEquipments();
        }
      } catch (error) {
        toast.error('Erro ao remover associação');
      }
    }
  };



  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'success';
      case 'expiring': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getAlertTypeColor = (type) => {
    switch (type) {
      case 'always': return 'primary';
      case 'expired_only': return 'error';
      case 'expiring_soon': return 'warning';
      default: return 'default';
    }
  };



  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Associações Equipamento-Barreira
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Sistema de Alertas Inteligente:</strong> Visualize as associações entre equipamentos e barreiras. Para criar novas associações, use o CRUD de Equipamentos.
      </Alert>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Associações Ativas
              </Typography>
              <Typography variant="h4">
                {associations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Alertas Enviados
              </Typography>
              <Typography variant="h4">
                {associations.reduce((sum, a) => sum + a.alerts_sent, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Equipamento</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Barreiras</TableCell>
              <TableCell>Tipo de Alerta</TableCell>
              <TableCell>Última Detecção</TableCell>
              <TableCell>Alertas / Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {associations.map((association) => (
              <TableRow key={association.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Security sx={{ mr: 1 }} />
                    {association.equipment_name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`common.${association.equipment_status}`)}
                    color={getStatusColor(association.equipment_status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {association.barrier_names.map((name, index) => (
                    <Chip
                      key={index}
                      label={name}
                      size="small"
                      sx={{ mr: 0.5 }}
                      variant="outlined"
                    />
                  ))}
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`equipment.${association.alert_type === 'always' ? 'always' : association.alert_type === 'expired_only' ? 'expiredOnly' : association.alert_type === 'expiring_soon' ? 'expiringSoon' : 'never'}`)}
                    color={getAlertTypeColor(association.alert_type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {association.last_detection ? 
                    new Date(association.last_detection).toLocaleString('pt-BR') : 
                    'Nunca'
                  }
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box display="flex" alignItems="center">
                      <Notifications sx={{ mr: 1, fontSize: 16 }} />
                      {association.alerts_sent}
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(association.id)}
                    >
                      Remover
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


    </Box>
  );
}

export default EquipmentBarriers;